/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/pickupGroupsApi.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    API calls for Pickup Groups (fetch list, fetch by ID, create, update, delete).
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { PickupGroup, PickupGroupPayload } from '../types';

export interface FetchPickupGroupsParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}

export interface PickupGroupsListResponse {
  success: boolean;
  data: PickupGroup[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PickupGroupDetailResponse {
  success: boolean;
  data: PickupGroup;
  message?: string;
}

export async function fetchPickupGroups(params: FetchPickupGroupsParams = {}): Promise<PickupGroupsListResponse> {
  const queryParams = new URLSearchParams({
    page: (params.page || 1).toString(),
    limit: (params.limit || 10).toString(),
    sortBy: params.sortBy || 'name',
    sortOrder: params.sortOrder || 'asc',
    ...(params.search && { search: params.search })
  });

  // Add domain filter if provided
  if (params.domainId) {
    queryParams.append('domainId', params.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/telephony/pickup-groups?${queryParams}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch pickup groups');
  }

  const result = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch pickup groups');
  }

  return result;
}

export async function fetchPickupGroupById(id: string | number): Promise<PickupGroup> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/pickup-groups/${id}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch pickup group');
  }

  const result: PickupGroupDetailResponse = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch pickup group');
  }

  return result.data;
}

export async function createPickupGroup(payload: PickupGroupPayload): Promise<PickupGroup> {
  const response = await fetch(
    buildApiUrl('/api/telephony/pickup-groups'),
    {
      method: 'POST',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to create pickup group');
  }

  const result: PickupGroupDetailResponse = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to create pickup group');
  }

  return result.data;
}

export async function updatePickupGroup(id: string | number, payload: PickupGroupPayload): Promise<PickupGroup> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/pickup-groups/${id}`),
    {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to update pickup group');
  }

  const result: PickupGroupDetailResponse = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update pickup group');
  }

  return result.data;
}

export async function deletePickupGroup(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/pickup-groups/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to delete pickup group');
  }
}

